import React, { FC, CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useFilters } from "../contexts/FiltersContext";
import { apiUrl } from "../utils/api";
import { StyledSelect, dropdownStyle } from "../components/StyledSelect";
import settingsIcon from "../components/settings.png";
import settingsIconActive from "../components/settings_act.png";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

// --- Type Definitions ---

// --- Types ---
interface StationItem { id: string; name: string }
interface ParameterOption { key: string; label: string; unit?: string; name?: string }
interface ColorSlot { color: string; key?: string } // assigned key (param or station)
interface ChartPoint { ts: string; t: number; [seriesKey: string]: number | string }

// --- API Types ---
interface DrillingParamsResponse {
  code: number;
  message: string;
  param_list: Array<{ id: string; name: string; name_human: string; unit?: string; description?: string }>;
}
interface UserStationsResponse {
  code: number;
  message: string;
  station_list: StationItem[];
}
interface DrillingResponse {
  code: number;
  message: string;
  station_list?: StationItem[];
  timestamp_list: string[];
  telemetry_data: Array<{ name: string; point_list: string[] }>;
}

// --- Utilities ---
const COLORS_10 = [
  "#5DADE2", // green
  "#EC7063", // orange
  "#58D68D", // blue
  "#F8C471", // cyan
  "#AF7AC5", // amber
  "#48C9B0", // red
  "#F06292", // mint
  "#AEB6BF", // dark red
  "#EB984E", // indigo
  "#A8D5F7", // purple
];

const formatTs = (d: Date) => `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getFullYear()).slice(-2)} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
const fmtIso = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return formatTs(d);
};
const toNumber = (s?: string | number, def = 0) => {
  if (typeof s === 'number') return s;
  if (s == null) return def;
  const n = Number(String(s).replace(',', '.'));
  return Number.isFinite(n) ? n : def;
};

// Deterministic mock generator based on keys
const genMockSeries = (key: string, stationId: string, points = 120): ChartPoint[] => {
  const seed = Array.from(`${key}|${stationId}`).reduce((a,c)=>a + c.charCodeAt(0), 0);
  const data: ChartPoint[] = [];
  const start = new Date();
  start.setHours(start.getHours() - 6); // 6 hours window
  for (let i = 0; i < points; i++) {
    const t = new Date(start.getTime() + i * (6 * 60 * 60 * 1000 / points));
    const base = 100 + 50 * Math.sin((i + seed) * 0.08);
    const noise = (seed % 17) * Math.cos((i + seed) * 0.03);
    const value = Math.max(0, Math.round(base + noise));
    data.push({ ts: formatTs(t), t: t.getTime(), [`${key}|${stationId}`]: value });
  }
  return data;
};

// --- Modal Component ---
const Modal: FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  contentStyle?: CSSProperties;
}> = ({ title, onClose, children, contentStyle }) => (
  <div style={styles.modalOverlay}>
    <div style={{ ...styles.modalContent, ...contentStyle }}>
      <div style={styles.modalHeader}>
        <h3 style={styles.modalTitle}>{title}</h3>
        <button aria-label="Закрыть" style={styles.modalCloseButton} onClick={onClose}>×</button>
      </div>
      {children}
    </div>
  </div>
);

// --- Components ---
const StationPickerBar: FC<{
  stations: StationItem[];
  selected: string[];
  onChange: (ids: string[]) => void;
}> = ({ stations, selected, onChange }) => {
  const addRef = useRef<HTMLDivElement>(null);
  const [addOpen, setAddOpen] = useState(false);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!addRef.current) return;
      if (!addRef.current.contains(e.target as Node)) setAddOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const available = stations.filter(s => !selected.includes(s.id));

  const updateAt = (idx: number, id: string) => {
    const next = selected.slice();
    next[idx] = id;
    onChange(next);
  };
  const removeAt = (idx: number) => {
    if (selected.length <= 1) return; // keep at least one
    const next = selected.slice();
    next.splice(idx, 1);
    onChange(next);
  };
  const addStation = (id: string) => {
    onChange([...selected, id]);
    setAddOpen(false);
  };

  return (
    <div style={styles.stationBar}>
      {selected.map((id, idx) => {
        const options = stations.filter(s => s.id === id || !selected.includes(s.id));
        return (
          <div key={id + idx} style={styles.selectGroup}>
            <StyledSelect
              value={id}
              onChange={(e) => updateAt(idx, (e.target as HTMLSelectElement).value)}
              style={{ ...dropdownStyle, width: 220, height: 28 }}
            >
              {options.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.name}</option>
              ))}
            </StyledSelect>
            <button
              title="Убрать станок"
              style={{
                ...styles.removeButton,
                ...(selected.length <= 1 ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
              }}
              disabled={selected.length <= 1}
              onClick={() => removeAt(idx)}
            >
              ✕
            </button>
          </div>
        );
      })}
      <div style={{ position: 'relative' }} ref={addRef}>
        <button
          style={{
            ...styles.addStationButton,
            ...(available.length === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
          }}
          disabled={available.length === 0}
          onClick={() => setAddOpen(o => !o)}
        >
          Добавить станок <span style={{ marginLeft: 6, opacity: 0.7 }}>▾</span>
        </button>
        {addOpen && (
          <div style={styles.addMenu}>
            {available.map(s => (
              <div key={s.id} style={styles.addMenuItem} onClick={() => addStation(s.id)}>
                {s.name}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Parameters sidebar (right panel)
const ParamsSidebar: FC<{
  open: boolean;
  onClose: () => void;
  params: ParameterOption[];
  selected: string[];
  onToggle: (key: string) => void;
  max: number;
  multiStation: boolean;
  getColorForParam: (key: string) => string | undefined;
}> = ({ open, onClose, params, selected, onToggle, max, multiStation, getColorForParam }) => {
  if (!open) return null;
  const canAddMore = selected.length < max;
  return (
    <aside style={styles.paramsSidebar}>
      <div style={styles.paramsHeader}>
        <div style={{ fontWeight: 700 }}>Параметры</div>
        <button style={styles.closeButton} onClick={onClose}>✕</button>
      </div>
      <div style={{ color: '#aaa', marginBottom: 12, fontSize: 12 }}>
        {multiStation ? 'Выберите один параметр для сравнения между станками.' : `Можно выбрать до ${max} параметров.`}
      </div>
      <div style={styles.paramsList}>
        {params.map(p => {
          const checked = selected.includes(p.key);
          const disabled = multiStation ? false : (!checked && !canAddMore);
          const color = checked ? getColorForParam(p.key) : undefined;
          const inputType = multiStation ? 'radio' : 'checkbox';
          return (
            <label key={p.key} style={{ ...styles.paramRow, opacity: disabled ? 0.6 : 1 }}>
              <input
                type={inputType as any}
                name="paramChoice"
                checked={checked}
                disabled={disabled}
                onChange={() => onToggle(p.key)}
              />
              <span style={styles.paramLabel}>
                {p.label}{p.unit ? `, ${p.unit}` : ''}
              </span>
              {color && <span style={{ ...styles.paramDot, background: color }} />}
            </label>
          );
        })}
      </div>
    </aside>
  );
};

// --- Page ---
const DrillingChartsPage: FC = () => {
  const { user } = useAuth();
  const { dateFrom, dateTo } = useFilters();
  const token = user?.access_token;

  // Station list from API
  const [stations, setStations] = useState<StationItem[]>([]);
  const [selectedStations, setSelectedStations] = useState<string[]>([]);

  // Parameter catalogue from API
  const [paramsList, setParamsList] = useState<ParameterOption[]>([]);

  const [selectedParams, setSelectedParams] = useState<string[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Color slots for parameters (single-station mode)
  const [paramSlots, setParamSlots] = useState<ColorSlot[]>(() => COLORS_10.map(c => ({ color: c })));
  const [paramStickyMap, setParamStickyMap] = useState<Record<string, number>>({});
  // Color slots for stations (multi-station mode)
  const [stationSlots, setStationSlots] = useState<ColorSlot[]>(() => COLORS_10.map(c => ({ color: c })));

  const multiStation = selectedStations.length > 1;
  const MAX_PARAMS = 10;

  // Upload modal state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Handle station selection changes; when switching from single → multiple,
  // reset parameters to a single active one (keep the first if present).
  const handleStationsChange = (ids: string[]) => {
    const wasSingle = selectedStations.length === 1;
    const nowMulti = ids.length > 1;
    setSelectedStations(ids);
    if (wasSingle && nowMulti) {
      setSelectedParams(prev => {
        const first = prev[0] || paramsList[0]?.key;
        return [first];
      });
    }
  };

  const toggleParam = (key: string) => {
    setSelectedParams(prev => {
      const exists = prev.includes(key);
      if (multiStation) {
        if (exists) return prev.filter(k => k !== key);
        if (prev.length >= 1) return prev; // restrict to one
        return [...prev, key];
      }
      if (exists) return prev.filter(k => k !== key);
      if (prev.length >= MAX_PARAMS) return prev;
      return [...prev, key];
    });
  };

  // Maintain sticky color assignment across toggles in single-station mode
  useEffect(() => {
    if (multiStation) return;
    // Build next slots based on selected params, existing slots, and sticky map
    const prevSlots = paramSlots;
    const prevSticky = paramStickyMap;
    const nextSlots: ColorSlot[] = COLORS_10.map((c, i) => ({ color: c }));
    const occupied = new Set<number>();

    // 1) Keep current positions for already assigned selected params
    selectedParams.forEach(k => {
      const idx = prevSlots.findIndex(s => s.key === k);
      if (idx !== -1) {
        nextSlots[idx] = { color: COLORS_10[idx], key: k };
        occupied.add(idx);
      }
    });

    // 2) Use sticky slot if available (and not occupied)
    selectedParams.forEach(k => {
      if (nextSlots.some(s => s.key === k)) return;
      const stickyIdx = prevSticky[k];
      if (stickyIdx != null && !occupied.has(stickyIdx)) {
        nextSlots[stickyIdx] = { color: COLORS_10[stickyIdx], key: k };
        occupied.add(stickyIdx);
      }
    });

    // 3) Fill remaining selected params into first free slots and update sticky mapping
    const newSticky: Record<string, number> = { ...prevSticky };
    selectedParams.forEach(k => {
      if (nextSlots.some(s => s.key === k)) return;
      const freeIdx = nextSlots.findIndex(s => !s.key);
      if (freeIdx !== -1) {
        nextSlots[freeIdx] = { color: COLORS_10[freeIdx], key: k };
        newSticky[k] = freeIdx;
        occupied.add(freeIdx);
      }
    });

    // Only update state if changed
    const changed = nextSlots.length !== prevSlots.length || nextSlots.some((s, i) => s.key !== prevSlots[i]?.key);
    if (changed) setParamSlots(nextSlots);
    // Sticky map might grow as we assign new params
    const stickyChanged = Object.keys(newSticky).length !== Object.keys(prevSticky).length || Object.keys(newSticky).some(k => newSticky[k] !== prevSticky[k]);
    if (stickyChanged) setParamStickyMap(newSticky);
  }, [selectedParams, multiStation]);

  // Assign station slots in multi-station mode
  useEffect(() => {
    if (!multiStation) return;
    setStationSlots(COLORS_10.map((c, i) => ({ color: c, key: selectedStations[i] })));
  }, [selectedStations, multiStation]);

  const [chartData, setChartData] = useState<ChartPoint[]>([]);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  // Fetch stations and params on token
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(apiUrl('/user-stations'), {
          method: 'GET',
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data: UserStationsResponse = await res.json();
          const list = Array.isArray(data.station_list) ? data.station_list : [];
          setStations(list);
          if (list.length && selectedStations.length === 0) setSelectedStations([list[0].id]);
        }
      } catch (e) { console.error(e); }
      try {
        const res = await fetch(apiUrl('/drilling/params'), {
          method: 'GET',
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data: DrillingParamsResponse = await res.json();
          const params = (data.param_list || []).map(p => ({ key: p.id, label: p.name_human || p.name || p.id, unit: p.unit, name: p.name }));
          setParamsList(params);
          if (params.length && selectedParams.length === 0) setSelectedParams([params[0].key]);
        }
      } catch (e) { console.error(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Fetch chart data on selection changes
  useEffect(() => {
    if (!token) return;
    if (!selectedStations.length) return;
    if (!selectedParams.length && paramsList.length) setSelectedParams([paramsList[0].key]);
    const from = dateFrom?.toISOString();
    const to = dateTo?.toISOString();
    const fetchForStation = async (stationId: string, paramIds: string[]): Promise<{ ts: string[]; series: Record<string, number[]> }> => {
      const qs = new URLSearchParams();
      qs.set('station_id', stationId);
      if (from) qs.set('period_from', from);
      if (to) qs.set('period_to', to);
      paramIds.forEach(id => qs.append('param_id', id));
      const url = `${apiUrl('/drilling/data')}?${qs.toString()}`;
      const res = await fetch(url, { headers: { Accept: 'application/json', Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`drilling ${res.status}`);
      const data: DrillingResponse = await res.json();
      const tsList = Array.isArray(data.timestamp_list) ? data.timestamp_list : [];
      const series: Record<string, number[]> = {};
      for (const item of data.telemetry_data || []) {
        series[item.name] = (item.point_list || []).map(v => toNumber(v, 0));
      }
      return { ts: tsList, series };
    };

    (async () => {
      try {
        if (!multiStation) {
          const st = selectedStations[0];
          if (selectedParams.length <= 1) {
            const { ts, series } = await fetchForStation(st, selectedParams);
            const rows: ChartPoint[] = ts.map((iso, i) => {
              const row: ChartPoint = { ts: fmtIso(iso), t: Date.parse(iso) };
              Object.keys(series).forEach(key => { (row as any)[key] = series[key][i] ?? null; });
              return row;
            });
            setChartData(rows);
          } else {
            // Request each parameter separately to improve backend compatibility
            const parts = await Promise.all(selectedParams.map(pid => fetchForStation(st, [pid])));
            const mapIso = new Map<string, ChartPoint>();
            parts.forEach(part => {
              const keys = Object.keys(part.series);
              part.ts.forEach((iso, i) => {
                const row = mapIso.get(iso) || { ts: fmtIso(iso), t: Date.parse(iso) };
                keys.forEach(k => { (row as any)[k] = part.series[k][i] ?? null; });
                mapIso.set(iso, row);
              });
            });
            const rows = Array.from(mapIso.entries())
              .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
              .map(([_, v]) => v);
            setChartData(rows);
          }
        } else {
          const paramKey = selectedParams[0] || paramsList[0]?.key;
          if (!paramKey) return;
          const maps = await Promise.all(selectedStations.map(st => fetchForStation(st, [paramKey])));
          const mapRows = new Map<string, ChartPoint>();
          maps.forEach((m, idx) => {
            const stId = selectedStations[idx];
            const stKey = stId;
            const valuesArrays = Object.values(m.series);
            const values = valuesArrays[0] || [];
            m.ts.forEach((iso, i) => {
              const row = mapRows.get(iso) || { ts: fmtIso(iso), t: Date.parse(iso) };
              (row as any)[stKey] = values[i] ?? null;
              mapRows.set(iso, row);
            });
          });
          const rows = Array.from(mapRows.entries())
            .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
            .map(([_, v]) => v);
          setChartData(rows);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedStations, selectedParams, multiStation, dateFrom, dateTo, paramsList]);

  // Lines config
  const lines = useMemo(() => {
    if (!multiStation) {
      // param lines
      return selectedParams.map((k, idx) => {
        const slot = paramSlots.find(s => s.key === k);
        const color = slot?.color || COLORS_10[idx % COLORS_10.length];
        const p = paramsList.find(p => p.key === k);
        const dataKey = p?.name || k; // backend telemetry uses machine `name`
        return { dataKey, color, name: p?.label || k, yAxisId: dataKey };
      });
    }
    // station lines
    return selectedStations.map((id, idx) => {
      const slot = stationSlots.find(s => s.key === id);
      const color = slot?.color || COLORS_10[idx % COLORS_10.length];
      const name = stations.find(s => s.id === id)?.name || id;
      return { dataKey: id, color, name, yAxisId: id };
    });
  }, [selectedParams, selectedStations, multiStation, paramSlots, stationSlots, stations, paramsList]);

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)' }}>
      {/* Header controls */}
      <div style={styles.headerBar}>
        <StationPickerBar stations={stations} selected={selectedStations} onChange={handleStationsChange} />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            title="Загрузить данные"
            style={styles.settingsIconButton}
            onClick={() => setShowUpload(true)}
          >
            📥
          </button>
          <div
            onClick={() => setIsSidebarOpen(s => !s)}
            title="Настройки"
            style={{
              ...styles.settingsIconButton,
              ...(isSidebarOpen ? { background: '#2E3749' } : {}),
            }}
          >
            <img src={isSidebarOpen ? settingsIconActive : settingsIcon} alt="Настройки" style={{ width: 20, height: 20 }} />
          </div>
        </div>
      </div>

      {/* Single large chart */}
      <div style={styles.chartCard}>
        <div style={styles.chartTitleRow}>
          <div style={{ fontWeight: 700 }}>Графики</div>
          <div style={{ color: '#aaa', fontSize: 12 }}>
            {multiStation ? 'Режим сравнения: один параметр, несколько станков' : 'Режим параметров: до 10 параметров для одного станка'}
          </div>
        </div>
        <div style={{ flex: 1, height: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
              onMouseMove={(state: any) => {
                if (state && state.isTooltipActive && typeof state.chartX === 'number' && typeof state.chartY === 'number') {
                  setTooltipPos({ x: state.chartX, y: state.chartY });
                }
              }}
              onMouseLeave={() => setTooltipPos(null)}
            >
              <CartesianGrid stroke="#2E3749" strokeDasharray="4 4" />
              <XAxis
                dataKey="t"
                type="number"
                domain={["dataMin", "dataMax"]}
                tickFormatter={(v: number) => formatTs(new Date(v))}
                tick={{ fill: '#D4DAE3', fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              {lines.map(l => (
                <YAxis
                  key={l.yAxisId}
                  yAxisId={l.yAxisId}
                  orientation="left"
                  stroke={l.color}
                  tick={{ fill: l.color, fontSize: 12 }}
                  tickLine={false}
                  axisLine={true}
                  domain={["auto", "auto"]}
                  width={48}
                />
              ))}
              <Tooltip
                position={tooltipPos ?? undefined}
                contentStyle={{ background: '#1F2534', border: '1px solid #2E3749' }}
                labelStyle={{ color: '#fff' }}
                labelFormatter={(label: any, _payload: any) => (
                  typeof label === 'number' ? formatTs(new Date(label)) : String(label)
                )}
                cursor={{ stroke: '#2E3749' }}
              />
              <Legend wrapperStyle={{ color: '#fff' }} />
              {lines.map(l => (
                <Line
                  key={l.dataKey}
                  type="monotone"
                  dataKey={l.dataKey}
                  yAxisId={l.yAxisId}
                  stroke={l.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3 }}
                  name={l.name}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <ParamsSidebar
          open={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          params={paramsList}
          selected={selectedParams}
          onToggle={(key) => {
            if (multiStation) {
              setSelectedParams([key]);
            } else {
              toggleParam(key);
            }
          }}
          max={MAX_PARAMS}
          multiStation={multiStation}
          getColorForParam={(key) => paramSlots.find(s => s.key === key)?.color}
        />
      </div>

      {/* Params sidebar is inside chartCard */}

      {showUpload && (
        <Modal
          title="Импорт данных"
          onClose={() => {
            setShowUpload(false);
            setUploadFile(null);
            setUploadError('');
          }}
        contentStyle={styles.importModal}
      >
        <div style={styles.importModalBody}>
          <label
            style={styles.importDropzone}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const droppedFile = event.dataTransfer.files?.[0];
              if (droppedFile) {
                setUploadFile(droppedFile);
                setUploadError('');
              }
            }}
          >
            <span style={styles.importDropzoneIcon} />
            <span style={styles.importDropzoneText}>{uploadFile ? uploadFile.name : 'Загрузите файл'}</span>
            <input
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={(event) => {
                setUploadFile(event.target.files ? event.target.files[0] : null);
                setUploadError('');
              }}
            />
          </label>

          <div style={styles.importFormGrid}>
            <div style={styles.importRow}>
              <label style={styles.importLabel}>Буровая</label>
              <select
                style={styles.importInput}
                defaultValue={selectedStations[0] || ''}
              >
                {stations.length ? (
                  stations.map((station) => (
                    <option key={station.id} value={station.id}>{station.name || station.id}</option>
                  ))
                ) : (
                  <option value="">Буровые не найдены</option>
                )}
              </select>
            </div>
          </div>

          {uploadError && <div style={styles.importError}>{uploadError}</div>}

          <div style={styles.importFooter}>
            <button
              type="button"
              style={styles.importCancelButton}
              onClick={() => {
                setShowUpload(false);
                setUploadFile(null);
                setUploadError('');
              }}
            >
              Отмена
            </button>
            <button
              type="button"
              style={{ ...styles.importSubmitButton, ...(uploading ? styles.importSubmitButtonDisabled : {}) }}
              disabled={uploading}
              onClick={async () => {
                if (uploading) return;
                if (!uploadFile) {
                  setUploadError('Выберите CSV-файл для импорта.');
                  return;
                }
                if (!selectedStations[0]) {
                  setUploadError('Выберите буровую.');
                  return;
                }

                setUploading(true);
                setUploadError('');

                try {
                  const form = new FormData();
                  form.append('file', uploadFile);

                  const params = new URLSearchParams();
                  params.set('station_id', selectedStations[0]);

                  const response = await fetch(apiUrl(`/drilling/data/imports/?${params.toString()}`), {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: form,
                  });

                  const data = await response.json().catch(() => null);

                  if (!response.ok) {
                    throw new Error(data?.message || `Ошибка импорта файла: ${response.status}`);
                  }

                  if (data?.code !== undefined && data.code !== 0) {
                    throw new Error(data?.message || 'API импорта вернул ошибку');
                  }

                  setShowUpload(false);
                  setUploadFile(null);
                  setUploading(false);
                } catch (e) {
                  const message = e instanceof Error ? e.message : 'Не удалось импортировать файл';
                  setUploadError(message);
                  setUploading(false);
                }
              }}
            >
              {uploading ? 'Импортируется...' : 'Импортировать'}
            </button>
          </div>
        </div>
      </Modal>
      )}
    </div>
  );
};

// (export moved to bottom)

const styles: { [key: string]: CSSProperties } = {
  headerBar: {
    display: 'flex',
    alignItems: 'center',
    background: '#1F2534',
    borderRadius: 16,
    padding: '12px 16px',
    border: '1px solid #2E3749',
    boxShadow: '0 2px 16px #0000003D',
    marginBottom: 12,
  },
  stationBar: { display: 'flex', alignItems: 'center', gap: 8 },
  selectGroup: { display: 'flex', alignItems: 'center', gap: 8 },
  removeButton: {
    background: '#2C2C2C',
    color: '#fff',
    border: '1px solid #2F3136',
    borderRadius: 8,
    width: 28,
    height: 28,
    cursor: 'pointer',
  },
  addStationButton: {
    background: 'transparent',
    color: '#D4DAE3',
    border: 'none',
    borderRadius: 8,
    padding: '6px 10px',
    fontSize: 14,
    cursor: 'pointer',
  },
  addMenu: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    background: '#1F2534',
    border: '1px solid #2E3749',
    borderRadius: 12,
    boxShadow: '0 2px 16px #0000003D',
    padding: 8,
    zIndex: 10,
    minWidth: 200,
  },
  addMenuItem: {
    color: '#fff',
    padding: '6px 8px',
    borderRadius: 6,
    cursor: 'pointer',
    background: '#293145',
  },
  settingsIconButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    cursor: 'pointer',
    border: '1px solid #2F3136',
  },
  chartCard: {
    background: '#1F2534',
    border: '1px solid #2E3749',
    borderRadius: 16,
    boxShadow: '0 2px 16px #0000003D',
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    position: 'relative',
  },
  chartTitleRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  paramsSidebar: {
    position: 'absolute' as any,
    top: 12,
    right: 12,
    bottom: 12,
    width: 320,
    background: '#1F2534',
    border: '1px solid #2E3749',
    borderRadius: 12,
    boxShadow: '0 2px 16px #0000003D',
    padding: 12,
    overflow: 'hidden',
  },
  paramsHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12,
  },
  closeButton: {
    background: 'transparent', border: 'none', color: '#fff', fontSize: 16, cursor: 'pointer',
  },
  paramsList: {
    overflowY: 'auto' as any,
    paddingRight: 4,
    maxHeight: 'calc(100% - 48px)',
  },
  paramRow: {
    display: 'flex', alignItems: 'center', gap: 8, color: '#fff', background: '#293145', padding: 8, borderRadius: 8, marginBottom: 6,
  },
  paramLabel: { flex: 1 },
  paramDot: { width: 10, height: 10, borderRadius: '50%' },

  // Modal styles
  modalOverlay: {
    position: 'fixed',
    inset: 0 as any,
    background: 'rgba(6, 10, 18, 0.58)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    background: '#1F2534',
    border: '1px solid #2E3749',
    borderRadius: '12px',
    width: '520px',
    maxWidth: '90vw',
    padding: '16px',
    color: '#fff',
    boxShadow: '0 2px 16px #0000003D',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
  },
  modalCloseButton: {
    background: 'transparent',
    border: 'none',
    color: '#cfd3dc',
    fontSize: 20,
    cursor: 'pointer',
  },
  importModal: {
    width: '380px',
    maxWidth: 'calc(100vw - 40px)',
    padding: '18px',
    borderRadius: '4px',
    background: '#171D2C',
    border: '1px solid #222C3E',
    boxShadow: '0 18px 50px rgba(0, 0, 0, 0.36)',
  },
  importModalBody: {
    display: 'grid',
    gap: '20px',
  },
  importDropzone: {
    height: '124px',
    border: '1px dashed #344158',
    borderRadius: '3px',
    background: '#2B3548',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    cursor: 'pointer',
    color: '#FFFFFF',
    boxSizing: 'border-box',
  } as CSSProperties,
  importDropzoneIcon: {
    width: '13px',
    height: '13px',
    display: 'inline-block',
    borderRadius: '50%',
    border: '3px solid rgba(154, 165, 184, 0.34)',
    borderTopColor: '#98A4B8',
    boxSizing: 'border-box',
  } as CSSProperties,
  importDropzoneText: {
    color: '#FFFFFF',
    fontSize: '13px',
    fontWeight: 600,
  },
  importFormGrid: {
    display: 'grid',
    gap: '18px',
  },
  importRow: {
    display: 'grid',
    gridTemplateColumns: '112px 1fr',
    alignItems: 'center',
    gap: '18px',
  },
  importLabel: {
    color: '#8F9BB2',
    fontSize: '12px',
    fontWeight: 600,
  },
  importInput: {
    width: '100%',
    height: '30px',
    padding: '0 10px',
    background: '#2B3548',
    border: '1px solid #3A465C',
    borderRadius: '3px',
    color: '#FFFFFF',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
  } as CSSProperties,
  importError: {
    padding: '8px 10px',
    borderRadius: '3px',
    background: 'rgba(239, 68, 68, 0.12)',
    border: '1px solid rgba(239, 68, 68, 0.35)',
    color: '#ff9f9f',
    fontSize: '12px',
    lineHeight: 1.35,
  },
  importFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '6px',
    marginTop: '6px',
  },
  importCancelButton: {
    height: '28px',
    minWidth: '82px',
    padding: '0 14px',
    border: 'none',
    borderRadius: '2px',
    background: '#2A3344',
    color: '#FFFFFF',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
  },
  importSubmitButton: {
    height: '28px',
    minWidth: '130px',
    padding: '0 14px',
    border: 'none',
    borderRadius: '2px',
    background: '#1E4F92',
    color: '#FFFFFF',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
  },
  importSubmitButtonDisabled: {
    opacity: 0.48,
    cursor: 'not-allowed',
  },
};

export default DrillingChartsPage;
